import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthProvider";
import { useEffect, useState, useRef } from "react";
import { initializeSessionTimeout, disableSessionTimeout, enableSessionTimeout, isForcedReauthRequired } from "@/utils/sessionTimeout";
import { KeepAliveManager } from "@/components/auth/KeepAliveManager";

import { Toaster } from "@/components/ui/sonner";
import { ProtectedRoutes } from "@/components/routing/ProtectedRoutes";
import { PublicRoutes } from "@/components/routing/PublicRoutes";
import { useAuth } from "@/contexts/AuthContext";
import { ForcedReauthModal } from "@/components/auth/ForcedReauthModal";
import ErrorBoundary from "@/components/shared/ErrorBoundary";

const AnimatedRoutes = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, isLoading, user, profile, isAuthReady } = useAuth();
  const lastLocationRef = useRef(location.pathname);
  const [showForcedReauth, setShowForcedReauth] = useState(false);

  // Check for forced re-authentication requirement
  useEffect(() => {
    if (session && user && isForcedReauthRequired()) {
      console.log('[App] Forced re-authentication required');
      setShowForcedReauth(true);
    } else {
      setShowForcedReauth(false);
    }
  }, [session, user]);

  // Handle session timeout based on current page
  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath === '/suspended' || currentPath === '/deactivated') {
      disableSessionTimeout();
    } else {
      enableSessionTimeout();
    }
  }, [location.pathname]);

  // Handle authenticated user redirects only from auth pages - NO OTHER REDIRECTS
  useEffect(() => {
    // Only proceed if auth is ready
    if (!isAuthReady) {
      return;
    }

    // Allow redirects on auth state changes even if location hasn't changed
    const locationChanged = lastLocationRef.current !== location.pathname;
    lastLocationRef.current = location.pathname;

    if (session && profile) {
      const authPages = ['/login', '/signup'];
      
      
      // CRITICAL FIX: Do NOT redirect from /join - let it handle its own flow
      // Also don't redirect if we're currently on /join regardless of auth state
      if (authPages.includes(location.pathname) && location.pathname !== '/join') {
        const redirectTo = profile.profile_completed ? '/dashboard' : '/profile-setup';
        try {
          navigate(redirectTo, { replace: true });
        } catch (error) {
          console.error('[App] Error during navigation:', error);
        }
      }
    }
  }, [session, profile, location.pathname, isAuthReady, navigate]);

  // Show loading while auth is stabilizing
  if (!isAuthReady) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // CRITICAL: Always use PublicRoutes if we're on /join, regardless of auth state
  // This ensures the join flow can complete without interference
  const isOnJoinPage = location.pathname === '/join' || location.pathname.startsWith('/join');
  const shouldShowProtectedRoutes = !!session && !isOnJoinPage;

  console.log('[App] Routing decision:', {
    path: location.pathname,
    isOnJoinPage,
    hasSession: !!session,
    shouldShowProtectedRoutes
  });

  try {
    return (
      <>
        <ForcedReauthModal 
          isOpen={showForcedReauth}
          onClose={() => setShowForcedReauth(false)}
          userEmail={user?.email || undefined}
        />
        <div>
          {shouldShowProtectedRoutes ? (
            <ProtectedRoutes />
          ) : (
            <PublicRoutes />
          )}
        </div>
      </>
    );
  } catch (error) {
    console.error('[App] Error rendering routes:', error);
    return (
      <div className="min-h-screen bg-red-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-xl font-bold text-red-600">Routing Error</h1>
          <p className="text-red-500">Check console for details</p>
        </div>
      </div>
    );
  }
};

function AppWithProviders() {
  const [sessionTimeoutInitialized, setSessionTimeoutInitialized] = useState(false);
  
  useEffect(() => {
    // Initialize session timeout only once
    if (!sessionTimeoutInitialized) {
      const cleanup = initializeSessionTimeout();
      setSessionTimeoutInitialized(true);
      
      return () => {
        if (cleanup) {
          cleanup();
        }
      };
    }
  }, [sessionTimeoutInitialized]);

  try {
    return (
      <AuthProvider>
        <KeepAliveManager />
        <AnimatedRoutes />
        <Toaster />
      </AuthProvider>
    );
  } catch (error) {
    console.error('[App] Error in AppWithProviders:', error);
    return (
      <div className="min-h-screen bg-red-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-xl font-bold text-red-600">Application Error</h1>
          <p className="text-red-500">Check console for details</p>
        </div>
      </div>
    );
  }
}

const App = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppWithProviders />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
